import React from 'react';
import { View, StyleSheet, Pressable, Text } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import { ReviewFlow } from '../ReviewFlow';
import { selectPantrySnapshot } from '../../../redux/pantrySelectors';
import { updateItemAndSync } from '../../../redux/inventorySlice';
import { markPantryUpdated } from '../../../redux/pantrySyncSlice';
import { buildKeepFiveUpdates } from '../usePantryActions';
import { usePantryToast } from '../PantryToast';
import { trackEvent } from '../../../services/analyticsService';
import { Paprika, PaprikaSpacing, PaprikaTypography } from '../../../styles/theme';
import { RootState, AppDispatch } from '../../../redux/store';

interface ReviewPanelProps {
  onClose: () => void;
}

export function ReviewPanel({ onClose }: ReviewPanelProps) {
  const snapshot = useSelector(selectPantrySnapshot);
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const userId = useSelector((state: RootState) => state.auth?.user?.uid);
  const { flash } = usePantryToast();

  const handleKeep = (itemId: string) => {
    const updates = buildKeepFiveUpdates();
    dispatch(updateItemAndSync({ id: itemId, updates, userId }));
  };

  const handleToss = (itemId: string) => {
    const updates = { status: 'discarded' as const, consumedBeforeExpiry: false };
    dispatch(updateItemAndSync({ id: itemId, updates, userId }));
    // Silent toss: no individual toast
  };

  const handleDone = (reviewedCount: number) => {
    dispatch(markPantryUpdated());
    trackEvent('pantry_review_completed', { count: reviewedCount });
    flash(t('pantry.review.allDone'));
    onClose();
  };

  return (
    <View style={styles.root}>
      {/* Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>{t('pantry.review.entry')}</Text>
          <Text style={styles.subtitle}>
            {t('pantry.review.panelSub', {
              current: 1,
              total: snapshot.reviewList.length,
            })}
          </Text>
        </View>
        <Pressable onPress={onClose} hitSlop={8} style={styles.closeButton}>
          <Feather name="x" size={20} color={Paprika.textMuted} />
        </Pressable>
      </View>

      {/* Content */}
      <View style={styles.content}>
        <ReviewFlow
          list={snapshot.reviewList}
          onKeep={handleKeep}
          onToss={handleToss}
          onDone={handleDone}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: Paprika.background,
    borderLeftWidth: 1,
    borderLeftColor: Paprika.hairline,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: PaprikaSpacing.md,
    paddingHorizontal: PaprikaSpacing.md,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.hairline,
  },
  title: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textMuted,
  },
  closeButton: {
    padding: 8,
  },
  content: {
    flex: 1,
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: PaprikaSpacing.md,
  },
});
